import React from 'react';
import { Typography, Icon } from '@scale/scaleui';
import {
  useGetOpsHubNotifications,
  useDismissAllNotifications,
} from './_data/useOpsHubNotifications';
import NotificationMessage from './NotificationMessage';
import clsx from 'clsx';
import type INotificationSend from '@scale/shared/types/notifications/notificationSend';
import { GenAIOpsHubPaths } from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import { Link } from 'react-router-dom';
import { logViewAllClick } from './_data/telemetry';
import { useUser } from 'react-scale/components/ui/UserContext';

const NotificationDropDown: React.FC = () => {
  const { data, isLoading } = useGetOpsHubNotifications({
    limit: 10,
  });
  const notifications = data?.notifications || [];
  const { mutate: dismissAll, isLoading: markingAllAsRead } = useDismissAllNotifications();
  const user = useUser();
  const markAllAsRead = async () => {
    dismissAll();
  };

  return (
    <div className="absolute right-0 mt-2 w-[480px] bg-neutral-0 border border-neutral-200 shadow-lg rounded-lg z-50">
      <div className="flex justify-between items-center p-4 border-b border-neutral-200">
        <Typography.Subtitle3 className="font-medium text-neutral-900 text-sm leading-5 font-inter truncate">
          Notifications
        </Typography.Subtitle3>
        {notifications.length ? (
          <button
            onClick={event => {
              event.stopPropagation();
              void markAllAsRead();
            }}
            className={clsx(
              'text-[#613EE0] text-xs font-medium font-inter hover:underline flex items-center',
              markingAllAsRead ? 'opacity-75' : '',
            )}
            disabled={markingAllAsRead}
          >
            {markingAllAsRead ? 'Marking...' : 'Mark all as read'}
          </button>
        ) : null}
      </div>
      {isLoading ? (
        <div className="h-[380px]">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="p-4 border-b border-neutral-200 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 bg-neutral-100 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <div className="h-4 bg-neutral-100 rounded w-1/4 mb-2" />
                  <div className="h-4 bg-neutral-100 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-neutral-100 rounded w-1/2" />
                </div>
                <div className="w-2 h-2 bg-neutral-100 rounded-full flex-shrink-0" />
              </div>
            </div>
          ))}
        </div>
      ) : !notifications.length ? (
        <div className="flex flex-col items-center justify-center h-[380px] text-neutral-500">
          <Icon
            icon="paper-plane"
            size="lg"
            className="h-16 w-16 mb-4 text-neutral-300 animate-[swing_1s_ease-in-out]"
          />
          <div className="text-xl font-medium mb-2">No notifications yet!</div>
          <div className="text-sm">We'll notify you when something important happens</div>
        </div>
      ) : (
        <>
          <ul className="h-[380px] overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:!bg-transparent [&::-webkit-scrollbar-track]:hover:!bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-neutral-300">
            {notifications.map((notification: INotificationSend) => (
              <NotificationMessage key={notification._id} notificationSend={notification} />
            ))}
          </ul>
          <div className="p-4 border-t border-neutral-200 flex justify-end">
            <Link
              to={GenAIOpsHubPaths.NotificationCenter}
              onClick={e => {
                e.stopPropagation();
                logViewAllClick(user);
              }}
              className="text-xs text-[#613EE0] hover:underline font-medium"
            >
              View All
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropDown;
